import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FC } from 'react';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useRouter } from 'next/router';
import AlertTemplate, { AlertTypes } from '@components/Alert/Alert';
import ConferenceOrderTicket from '@components/ConferenceOrderTickets/ConferenceOrderTicket';
import useTranslations from '@hooks/useTranslation';
import CheckboxTemplate from '@ui/Checkbox/Checkbox';
import { EMAIL_REGEX, ROUTES, getCaptchaKeys } from '../../constants';
import { COMPONENT_TYPE, IColors, IConferenceItem } from '../../interfaces/Conference';
import { calendarApi } from '../../services/calendar.service';
import {
    Agreements,
    ButtonSendForm,
    Buttons,
    CheckboxLabel,
    Col1,
    Col2,
    DiscountCode,
    DiscountLabel,
    Error,
    Input,
    InputSection,
    Option,
    OrderForm,
    OrderFormCompany,
    OrderFormDiscount,
    OrderFormPerson,
    OrderFormTickets,
    OrderGeneralText,
    OrderTitleText,
    Select,
    Ticket,
    TicketButtons,
    TicketPrice,
    TicketTitle,
    Title,
    TitleSymbol,
    Wrapper,
} from './ConferenceOrderTickets.elements';

export enum STATES {
    'Česká Republika' = 'CZ',
    'Slovensko' = 'SK',
}

interface ConferenceProps {
    colors?: IColors;
    conf?: IConferenceItem;
}

const ConferenceTicketOrderTemplate: FC<ConferenceProps> = ({ colors, conf }) => {
    const { t } = useTranslations();

    const router = useRouter();

    const [ticketsToMail, setTicketsToMail] = useState([]);
    const [totalPrice, setTotalPrice] = useState(0);

    const [isSent, setIsSent] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [hasSentError, setHasSentError] = useState(false);
    const [token, setToken] = useState(null);
    const [missingData, setMissingData] = useState(false);

    const [hasEmailError, setHasEmailError] = useState(false);
    const [hasFirstNameError, setHasFirstNameError] = useState(false);
    const [hasPhoneError, setHasPhoneError] = useState(false);
    const [hasBusinessAddressError, setHasBusinessAddressError] = useState(false);
    const [hasBusinessCityError, setHasBusinessCityError] = useState(false);
    const [hasBusinessPostalCodeError, setHasBusinessPostalCodeError] = useState(false);
    const [hasBillingDetails, setHasBillingDetails] = useState(false);
    const [hasAgreement, setHasAgreement] = useState(false);
    const [hasBusinessNameError, setHasBusinessNameError] = useState(false);
    const [hasBusinessIdError, setHasBusinessIdError] = useState(false);
    const [hasBusinessVatIdError, setHasBusinessVatIdError] = useState(false);
    const [hasBusinessVatNoError, setHasBusinessVatNoError] = useState(false);

    const emailInputRef = useRef(null);
    const firstNameInputRef = useRef(null);
    const discountInputRef = useRef(null);
    const phoneInputRef = useRef(null);
    const businessNameInputRef = useRef(null);
    const businessIdInputRef = useRef(null);
    const businessVatIdInputRef = useRef(null);
    const businessVatNoInputRef = useRef(null);
    const businessCountryInputRef = useRef(null);
    const businessAddressInputRef = useRef(null);
    const businessPostalCodeInputRef = useRef(null);
    const businessCityInputRef = useRef(null);

    useEffect(() => {
        let tmpPrice = 0;
        ticketsToMail.forEach((item) => (tmpPrice += item.totalPrice));
        setTotalPrice(tmpPrice);
    }, [ticketsToMail]);

    const handleMissingData = async () => {
        setMissingData(true), setTimeout(() => setMissingData(false), 3000);
    };

    const handleSubmitClick = async () => {
        setHasSentError(false);
        setHasEmailError(false);
        setHasFirstNameError(false);
        setHasPhoneError(false);
        setHasBusinessAddressError(false);
        setHasBusinessCityError(false);
        setHasBusinessPostalCodeError(false);
        setHasBusinessNameError(false);
        setHasBusinessIdError(false);
        setHasBusinessVatIdError(false);
        setHasBusinessVatNoError(false);
        setIsSent(false);

        if (firstNameInputRef?.current?.value === '') {
            setHasFirstNameError(true);
            handleMissingData();
            return;
        }

        if (emailInputRef?.current?.value === '' || !EMAIL_REGEX.test(emailInputRef?.current?.value)) {
            setHasEmailError(true);
            handleMissingData();
            return;
        }

        if (phoneInputRef?.current?.value === '') {
            setHasPhoneError(true);
            handleMissingData();
            return;
        } else if (phoneInputRef?.current?.value && !phoneInputRef?.current?.value?.match(/^[+]?[/0-9. -]{9,}$/)) {
            setHasPhoneError(true);
            handleMissingData();
            return;
        }

        if (businessAddressInputRef?.current?.value === '') {
            setHasBusinessAddressError(true);
            handleMissingData();
            return;
        }

        if (businessCityInputRef?.current?.value === '') {
            setHasBusinessCityError(true);
            handleMissingData();
            return;
        }

        if (businessPostalCodeInputRef?.current?.value === '') {
            setHasBusinessPostalCodeError(true);
            handleMissingData();
            return;
        } else if (
            businessPostalCodeInputRef?.current?.value &&
            !businessPostalCodeInputRef?.current?.value?.match(/^[0-9]{5}$/)
        ) {
            setHasBusinessPostalCodeError(true);
            handleMissingData();
            return;
        }

        if (hasBillingDetails && businessNameInputRef?.current?.value === '') {
            setHasBusinessNameError(true);
            handleMissingData();
            return;
        }

        if (hasBillingDetails && businessIdInputRef?.current?.value === '') {
            setHasBusinessIdError(true);
            handleMissingData();
            return;
        }

        if (businessCountryInputRef?.current?.value === 'Slovensko') {
            if (hasBillingDetails && businessVatIdInputRef?.current?.value === '') {
                setHasBusinessVatIdError(true);
                handleMissingData();
                return;
            } else if (
                hasBillingDetails &&
                businessVatIdInputRef?.current?.value &&
                !businessVatIdInputRef?.current?.value?.match(/^[0-9]{10}$/)
            ) {
                setHasBusinessVatIdError(true);
                handleMissingData();
                return;
            }
        } else if (businessCountryInputRef?.current?.value === 'Česká Republika') {
            if (hasBillingDetails && businessVatIdInputRef?.current?.value === '') {
                setHasBusinessVatIdError(true);
                handleMissingData();
                return;
            } else if (
                hasBillingDetails &&
                businessVatIdInputRef?.current?.value &&
                !businessVatIdInputRef?.current?.value?.match(/^[A-Z]{2}[0-9]{8,10}$/)
            ) {
                setHasBusinessVatIdError(true);
                handleMissingData();
                return;
            }
        }

        if (businessCountryInputRef?.current?.value === 'Slovensko') {
            if (hasBillingDetails && businessVatNoInputRef?.current?.value === '') {
                setHasBusinessVatNoError(true);
                handleMissingData();
                return;
            } else if (
                hasBillingDetails &&
                businessVatNoInputRef?.current?.value &&
                !businessVatNoInputRef?.current?.value?.match(/^[A-Z]{2}[0-9]{10}$/)
            ) {
                setHasBusinessVatNoError(true);
                handleMissingData();
                return;
            }
        } else {
            setHasBusinessVatNoError(false);
        }

        const data = {
            name: conf?.conference?.title,
            discount_code: discountInputRef?.current?.value,
            person: {
                email: emailInputRef?.current?.value,
                street: businessAddressInputRef?.current?.value,
                name: firstNameInputRef?.current?.value,
                city: businessCityInputRef?.current?.value,
                phone: phoneInputRef?.current?.value,
                postal_code: businessPostalCodeInputRef?.current?.value,
            },
            company: {
                name: businessNameInputRef?.current?.value,
                business_id: businessIdInputRef?.current?.value,
                ...(businessVatIdInputRef?.current?.value !== '' && { tax_id: businessVatIdInputRef?.current?.value }),
                ...(businessVatNoInputRef?.current?.value !== '' && { vat_no: businessVatNoInputRef?.current?.value }),
            },
            tickets: ticketsToMail,
            token,
        };

        try {
            setIsSending(true);
            await calendarApi.sendOrderForm(data);
            setIsSending(false);
            setIsSent(true);
        } catch (error: any) {
            setHasSentError(true);
            setIsSending(false);
        }
    };

    const handleSetCountTickets = (e) => {
        if (ticketsToMail.findIndex((item) => item.name === e.name) !== -1) {
            setTicketsToMail(
                ticketsToMail.map((previousHarmonogram) =>
                    previousHarmonogram.name === e.name
                        ? { count: e.count, name: e.name, totalPrice: e.totalPrice }
                        : previousHarmonogram
                )
            );
        } else {
            setTicketsToMail([...ticketsToMail, { count: e.count, name: e.name, totalPrice: e.totalPrice }]);
        }
    };

    const verifyRecaptchaCallback = useCallback((token) => {
        setToken(token);
    }, []);

    return (
        <Wrapper id={`confSection${COMPONENT_TYPE.ORDERFORMTICKET}`} colors={colors}>
            <Title colors={colors}>
                {t('conference.component.orderForm.title')}
                <TitleSymbol colors={colors} />
            </Title>

            <OrderGeneralText>
                V případě zájmu o účast, vás prosíme, abyste vyplnili objednávkový formulář. Pro zaslání faktury a
                vystavení vstupenky vás následně kontaktuje referentka našeho vzdělávání.
            </OrderGeneralText>

            <OrderForm>
                {conf?.conference?.tickets.length > 0 && (
                    <>
                        <OrderFormTickets colors={colors}>
                            <OrderTitleText>{t('conference.general.selectTicket')}</OrderTitleText>

                            {conf?.conference?.tickets?.map((item, index) => (
                                <ConferenceOrderTicket
                                    colors={colors}
                                    ticket={item}
                                    index={index}
                                    handleSetCountTickets={handleSetCountTickets}
                                    key={`orderTicket${index}`}
                                />
                            ))}

                            <Ticket footer>
                                <TicketTitle footer></TicketTitle>
                                <TicketPrice footer>{t('conference.general.total')}</TicketPrice>
                                <TicketButtons footer></TicketButtons>
                                <TicketPrice footer>
                                    {Math.round(totalPrice * 100) / 100} {t('conference.general.currencySign')}
                                </TicketPrice>
                            </Ticket>
                        </OrderFormTickets>
                    </>
                )}

                <OrderTitleText>{t('conference.general.yourData')}</OrderTitleText>
                <OrderFormPerson>
                    <Col1>
                        <InputSection>
                            <Input
                                className={hasFirstNameError ? 'error' : ''}
                                ref={firstNameInputRef}
                                placeholder={`${t('conference.general.firstnameSurname')}*`}
                            />
                            {hasFirstNameError && <Error>{t('modal.register.firstName.error')}</Error>}
                        </InputSection>

                        <InputSection>
                            <Input
                                className={hasEmailError ? 'error' : ''}
                                ref={emailInputRef}
                                placeholder={`${t('conference.general.email')}*`}
                            />
                            {hasEmailError && <Error>{t('modal.register.mail.error')}</Error>}
                        </InputSection>

                        <InputSection>
                            <Input
                                className={hasPhoneError ? 'error' : ''}
                                ref={phoneInputRef}
                                placeholder={`${t('conference.general.phone')}*`}
                            />
                            {hasPhoneError && <Error>{t('modal.register.phone.error')}</Error>}
                        </InputSection>
                    </Col1>

                    <Col2>
                        <InputSection>
                            <Input
                                className={hasBusinessAddressError ? 'error' : ''}
                                ref={businessAddressInputRef}
                                placeholder={`${t('conference.general.street')}*`}
                            />
                            {hasBusinessAddressError && <Error>{t('modal.register.address.error')}</Error>}
                        </InputSection>
                        <InputSection>
                            <Input
                                className={hasBusinessCityError ? 'error' : ''}
                                ref={businessCityInputRef}
                                placeholder={`${t('conference.general.city')}*`}
                            />
                            {hasBusinessCityError && <Error>{t('modal.register.city.error')}</Error>}
                        </InputSection>
                        <InputSection>
                            <Input
                                className={hasBusinessPostalCodeError ? 'error' : ''}
                                ref={businessPostalCodeInputRef}
                                placeholder={`${t('conference.general.postalCode')}*`}
                            />
                            {hasBusinessPostalCodeError && <Error>{t('modal.register.postalCode.error')}</Error>}
                        </InputSection>

                        <InputSection>
                            <Select bottom={30} top={10} ref={businessCountryInputRef} name="businessCountry">
                                {Object.keys(STATES).map((value, key) => (
                                    <Option key={key} value={value}>
                                        {value}
                                    </Option>
                                ))}
                            </Select>
                        </InputSection>
                    </Col2>
                </OrderFormPerson>

                <CheckboxTemplate
                    name="billing"
                    checked={hasBillingDetails}
                    onChange={() => setHasBillingDetails(!hasBillingDetails)}
                >
                    <CheckboxLabel colors={colors}>
                        Chcem zadať <strong>firemné údaje</strong>
                    </CheckboxLabel>
                </CheckboxTemplate>

                <OrderFormCompany>
                    {hasBillingDetails && (
                        <>
                            <Col1>
                                <InputSection>
                                    <Input
                                        className={hasBusinessNameError ? 'error' : ''}
                                        ref={businessNameInputRef}
                                        placeholder={`${t('conference.general.companyName')}*`}
                                    />
                                    {hasBusinessNameError && <Error>{t('general.fillCorrectShape')}</Error>}
                                </InputSection>
                                <InputSection>
                                    <Input
                                        className={hasBusinessIdError ? 'error' : ''}
                                        ref={businessIdInputRef}
                                        placeholder={`${t('conference.general.IDnumber')}*`}
                                    />
                                    {hasBusinessIdError && <Error>{t('general.fillCorrectShape')}</Error>}
                                </InputSection>
                            </Col1>
                            <Col2>
                                <InputSection>
                                    <Input
                                        className={hasBusinessVatIdError ? 'error' : ''}
                                        ref={businessVatIdInputRef}
                                        placeholder={`${t('conference.general.taxId')}*`}
                                    />
                                    {hasBusinessVatIdError && <Error>{t('general.fillCorrectShape')}</Error>}
                                </InputSection>
                                {businessCountryInputRef?.current?.value === 'Slovensko' && (
                                    <InputSection>
                                        <Input
                                            className={hasBusinessVatNoError ? 'error' : ''}
                                            ref={businessVatNoInputRef}
                                            placeholder={`${t('conference.general.taxIdVat')}*`}
                                        />
                                        {hasBusinessVatNoError && <Error>{t('general.fillCorrectShape')}</Error>}
                                    </InputSection>
                                )}
                            </Col2>
                        </>
                    )}
                </OrderFormCompany>

                <OrderFormDiscount>
                    <DiscountLabel>{t('conference.general.iHaveDiscountCode')}</DiscountLabel>
                    <DiscountCode>
                        <Input ref={discountInputRef} placeholder={t('conference.general.enterDiscountCode')} />
                    </DiscountCode>
                </OrderFormDiscount>

                <Agreements>
                    <CheckboxTemplate
                        name="personally"
                        checked={hasAgreement}
                        onChange={() => setHasAgreement(!hasAgreement)}
                    >
                        <CheckboxLabel colors={colors}>
                            Souhlasím so{' '}
                            <a onClick={() => router.push({ pathname: ROUTES.GDPR })}>zpracováním osobních údajů</a>
                        </CheckboxLabel>
                    </CheckboxTemplate>
                </Agreements>

                <GoogleReCaptcha onVerify={verifyRecaptchaCallback} />
            </OrderForm>

            <Buttons>
                <ButtonSendForm
                    colors={colors}
                    disabled={!hasAgreement || isSending || token}
                    onClick={handleSubmitClick}
                >
                    {isSending ? `${t('conference.general.sending')}` : `${t('conference.general.sendOrder')}`}
                </ButtonSendForm>
            </Buttons>
            {isSent && <AlertTemplate type={AlertTypes.success} message={t('conference.general.yourMessageWasSent')} />}
            {missingData && (
                <AlertTemplate type={AlertTypes.warning} message={t('conference.general.fillMissingData')} />
            )}
            {hasSentError && <AlertTemplate type={AlertTypes.error} message={t('contact.error')} />}
            <OrderGeneralText>
                StudioW zpracovává Vaše osobní údaje (jméno, příjmení, adrena, email, telefon) pouze za účelem vyřízení
                Vaší objednávky. Vaše údaje budou zpracovány v interních a systémech poučenými pracovníky po dobu 10 let
                a nebudou dále poskytnuty žádným třetím osobám.
            </OrderGeneralText>
        </Wrapper>
    );
};

export default ConferenceTicketOrderTemplate;
